import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { ENTITIES, TRUSTS, ENTITY_DISCLAIMER, TRUST_DISCLAIMER } from "@/lib/entities";
import { ChevronDown, Home, Building2, Landmark, ShieldCheck, Check, X, ArrowRight, Scale, Bookmark } from "lucide-react";

function Expandable({ item, kind, defaultOpen, recommended, pinned, onPin }) {
    const [open, setOpen] = useState(!!defaultOpen);
    const isEntity = kind === "entity";
    return (
        <div className={`si-card overflow-hidden ${recommended ? "ring-2 ring-teal" : item.popular ? "border-ocean/40" : ""}`} data-testid={`${kind}-card-${item.id}`}>
            <div className="flex w-full items-start justify-between gap-3 p-5">
                <button onClick={() => setOpen(!open)} className="flex flex-1 items-start justify-between gap-3 text-left" aria-expanded={open}>
                    <div>
                        <div className="flex flex-wrap items-center gap-2">
                            <h3 className="font-heading text-base font-bold text-navy">{item.name}</h3>
                            {recommended && <span className="rounded-full bg-teal/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-teal">Recommended for you</span>}
                            {item.popular && !recommended && <span className="rounded-full bg-ocean/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-ocean">Most common</span>}
                        </div>
                        <p className="mt-1 text-sm text-slate-600">{item.tagline}</p>
                    </div>
                    <ChevronDown className={`h-5 w-5 shrink-0 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
                </button>
                <button onClick={() => onPin(item, kind)} data-testid={`${kind}-pin-${item.id}`} aria-label={pinned ? "Unpin" : "Pin"} title={pinned ? "Unpin" : "Pin to My Plan"}
                    className={`shrink-0 rounded-lg p-1.5 transition-colors ${pinned ? "text-ocean" : "text-slate-300 hover:text-ocean"}`}>
                    <Bookmark className={`h-5 w-5 ${pinned ? "fill-current" : ""}`} />
                </button>
            </div>
            {open && (
                <div className="border-t border-slate-100 bg-slate-50/60 p-5 text-sm" data-testid={`${kind}-detail-${item.id}`}>
                    {isEntity ? (
                        <div className="grid gap-4 sm:grid-cols-2">
                            <Field label="Liability protection" value={item.liability} icon={ShieldCheck} />
                            <Field label="Taxation" value={item.taxation} icon={Scale} />
                            <Field label="Complexity" value={item.complexity} />
                            <Field label="Ownership" value={item.owners} />
                            <div className="sm:col-span-2 grid gap-4 sm:grid-cols-2">
                                <List title="Pros" items={item.pros} good />
                                <List title="Cons" items={item.cons} />
                            </div>
                            <Field label="Best for" value={item.bestFor} className="sm:col-span-2" />
                        </div>
                    ) : (
                        <div className="grid gap-4 sm:grid-cols-2">
                            <Field label="Control" value={item.control} />
                            <Field label="Protection" value={item.protection} icon={ShieldCheck} />
                            <Field label="Taxation" value={item.taxation} icon={Scale} />
                            <Field label="Best for" value={item.bestFor} />
                            <Field label="Important cautions" value={item.cautions} className="sm:col-span-2 rounded-lg bg-amber-50 p-3 !text-amber-900" />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function Field({ label, value, icon: Icon, className = "" }) {
    return (
        <div className={className}>
            <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-500">{Icon && <Icon className="h-3.5 w-3.5" />} {label}</div>
            <p className="mt-1 leading-relaxed text-slate-700">{value}</p>
        </div>
    );
}

function List({ title, items, good }) {
    return (
        <div>
            <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">{title}</div>
            <ul className="mt-1.5 space-y-1.5">
                {items.map((t) => (
                    <li key={t} className="flex items-start gap-2 text-slate-700">
                        {good ? <Check className="mt-0.5 h-4 w-4 shrink-0 text-teal" /> : <X className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />} {t}
                    </li>
                ))}
            </ul>
        </div>
    );
}

export default function Compare() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [params] = useSearchParams();
    const focus = (params.get("focus") || "").split(",").filter(Boolean);
    const [tab, setTab] = useState("entities");
    const [pinIds, setPinIds] = useState([]);
    const isEntities = tab === "entities";
    const items = isEntities ? ENTITIES : TRUSTS;

    useEffect(() => {
        if (user) api.get("/me/plan").then((r) => setPinIds((r.data.pins || []).map((p) => p.id))).catch(() => {});
    }, [user]);

    const togglePin = async (item, kind) => {
        if (!user) { toast.info("Sign in to pin structures to your plan."); navigate("/"); return; }
        try {
            const { data } = await api.post("/me/pins", { id: item.id, name: item.name, kind });
            setPinIds((data.pins || []).map((p) => p.id));
            toast.success(data.pinned ? "Pinned to My Plan" : "Removed from My Plan");
        } catch { toast.error("Could not update your pins."); }
    };

    return (
        <div className="min-h-screen bg-white" data-testid="compare-page">
            <div className="border-b border-slate-200 bg-navy px-4 py-4 text-white lg:px-8">
                <div className="mx-auto flex max-w-5xl items-center justify-between">
                    <button onClick={() => navigate("/")} className="flex items-center gap-2.5" data-testid="compare-home-link"><Scale className="h-5 w-5 text-teal-light" /><span className="font-heading text-sm font-bold">Smarter Choice · Compare structures</span></button>
                    <div className="flex items-center gap-2">
                        {user && <Button size="sm" variant="ghost" onClick={() => navigate("/my-plan")} className="text-white hover:bg-white/10"><Bookmark className="mr-1.5 h-4 w-4" /> My Plan</Button>}
                        <Button size="sm" variant="ghost" onClick={() => navigate("/")} className="text-white hover:bg-white/10"><Home className="mr-1.5 h-4 w-4" /> Home</Button>
                    </div>
                </div>
            </div>

            <div className="mx-auto max-w-5xl px-4 py-10 lg:px-8">
                <h1 className="font-heading text-3xl font-bold text-navy sm:text-4xl">Entities & Trusts — side by side</h1>
                <p className="mt-3 max-w-2xl text-slate-600">Understand how every common business structure and trust works so you can ask better questions — then confirm the right fit with a licensed attorney and CPA.</p>

                <div className="mt-6 inline-flex rounded-full border border-slate-200 bg-slate-100 p-1" role="tablist">
                    <button onClick={() => setTab("entities")} data-testid="compare-tab-entities" role="tab" aria-selected={isEntities}
                        className={`flex items-center gap-1.5 rounded-full px-5 py-2 text-sm font-semibold transition-colors ${isEntities ? "bg-navy text-white" : "text-slate-600 hover:text-navy"}`}>
                        <Building2 className="h-4 w-4" /> Business Entities ({ENTITIES.length})
                    </button>
                    <button onClick={() => setTab("trusts")} data-testid="compare-tab-trusts" role="tab" aria-selected={!isEntities}
                        className={`flex items-center gap-1.5 rounded-full px-5 py-2 text-sm font-semibold transition-colors ${!isEntities ? "bg-navy text-white" : "text-slate-600 hover:text-navy"}`}>
                        <Landmark className="h-4 w-4" /> Trusts ({TRUSTS.length})
                    </button>
                </div>

                <div className="mt-6 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="compare-disclaimer">
                    <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
                    <p className="text-sm leading-relaxed text-amber-900">{isEntities ? ENTITY_DISCLAIMER : TRUST_DISCLAIMER}</p>
                </div>

                <div className="mt-6 grid gap-4">
                    {items.map((it) => (
                        <Expandable key={it.id} item={it} kind={isEntities ? "entity" : "trust"}
                            defaultOpen={isEntities && focus.includes(it.id)}
                            recommended={isEntities && focus.includes(it.id)}
                            pinned={pinIds.includes(it.id)} onPin={togglePin} />
                    ))}
                </div>

                <div className="mt-10 rounded-[1.5rem] border border-slate-200 bg-navy p-8 text-white" data-testid="compare-cta">
                    <h3 className="font-heading text-2xl font-bold">Not sure which fits your goals?</h3>
                    <p className="mt-2 max-w-2xl text-slate-200">Our Smarter Intelligence Strategizer maps your situation to clear next steps, and our Done-With-You Business Launch helps you set it up with licensed professionals.</p>
                    <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                        <Button onClick={() => navigate("/strategizer")} data-testid="compare-strategizer-btn" className="h-12 bg-teal px-7 font-bold text-navy hover:bg-teal-light">Build my free roadmap <ArrowRight className="ml-2 h-4 w-4" /></Button>
                        <Button onClick={() => navigate("/services/business-launch")} variant="outline" data-testid="compare-service-btn" className="h-12 border-white/25 bg-transparent px-7 font-semibold text-white hover:bg-white/10">See Business Launch</Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
